/**
 * すでに重なっている答えの片づけ（決定 D4 の残り）。
 *
 * この面が守っていること:
 * - **見せてから消す。** 何本が消えるか・どの問いが消えるかを先に出し、
 *   確かめる段を 1 つ挟んでから実行する。押した瞬間には消えない
 * - **元に戻せないことを書く。** 消した答えは戻せない（同じルールで
 *   生成し直せば作り直せる、というところまで書く）
 * - 実行には、いま見せている件数をそのまま添える。サーバーが数え直した
 *   件数と食い違えば断られるので、**見ていない答えは消えない**
 * - 1 回で片づけられない量のときは、理由と件数を出して押せなくする
 */

import { useCallback, useState } from "react";
import { useMutation, useQuery } from "urql";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  FaqDuplicatesDocument,
  LoadingState,
  MergeDuplicateFaqsDocument,
  Tag,
  formatJstDateTime,
} from "visumo-data-agent-web-shared";
import { HelpIcon } from "../help/HelpIcon";

/** 1 回の片づけにつき 1 つ。押し直しで二重に走らせない。 */
function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export const FAQ_CLEANUP_NOTE =
  "同じ根拠から作られた、言い方だけが違う答えを 1 本にまとめます。消した答えは元に戻せません（同じルールで作り直すことはできます）。";

/**
 * 消える答えが 1 件も無いときの言い方。
 *
 * 畳み込みが入る前の答えは、**重なっていなくても**古い並びのまま残っている。
 * そのままにすると次の生成が別の項目を作って重なるので、並びだけをそろえ直す
 * 意味がある。ここで「片づける」「消します」と書くと、**消えないものを
 * 消すと読ませる**ことになる。
 */
export const FAQ_CLEANUP_REKEY_ONLY_LABEL = "並びをそろえ直す";

export const FAQ_CLEANUP_REMOVE_LABEL = "重なりを片づける";

export const FAQ_CLEANUP_OVER_LIMIT_NOTE =
  "この商品は、一度に片づけられる件数を超えています。分けて片づける仕組みが要るので、そのままにしてあります。";

export function FaqDuplicatePanel({
  productId,
  onFinished,
}: {
  productId: string;
  onFinished: () => void;
}) {
  const [result, refetch] = useQuery({
    query: FaqDuplicatesDocument,
    variables: { productId },
  });
  const [mergeResult, merge] = useMutation(MergeDuplicateFaqsDocument);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState<number | null>(null);

  const report = result.data?.faqDuplicates ?? null;

  const run = useCallback(async () => {
    if (report === null) return;
    const response = await merge({
      input: {
        productId,
        acknowledgedRemovedCount: report.removedCount,
        idempotencyKey: newIdempotencyKey(),
      },
    });
    const payload = response.data?.mergeDuplicateFaqs ?? null;
    if (payload !== null && payload.userErrors.length === 0) {
      setDone(payload.removedCount);
      setConfirming(false);
      refetch({ requestPolicy: "network-only" });
      // 商品の面（作れている答え）も引き直す。片づけの結果はそちらに出る
      onFinished();
    }
  }, [merge, onFinished, productId, refetch, report]);

  if (result.fetching && result.data === undefined) {
    return (
      <Card
        title="重なっている答え"
        extra={<HelpIcon slug="products/faq-cleanup" />}
      >
        <LoadingState />
      </Card>
    );
  }
  if (result.error !== undefined) {
    return (
      <Card
        title="重なっている答え"
        extra={<HelpIcon slug="products/faq-cleanup" />}
      >
        <ErrorState
          description={result.error.message}
          onRetry={() => refetch({ requestPolicy: "network-only" })}
        />
      </Card>
    );
  }
  if (report === null) {
    return null;
  }

  const userErrors = mergeResult.data?.mergeDuplicateFaqs.userErrors ?? [];
  const nothingToDo = report.removedCount === 0 && report.rekeyedCount === 0;
  // 消えるものが無いときは「片づける」と書かない(消えないものを消すと読ませない)
  const removes = report.removedCount > 0;
  const actionLabel = removes
    ? FAQ_CLEANUP_REMOVE_LABEL
    : FAQ_CLEANUP_REKEY_ONLY_LABEL;

  return (
    <Card
      title="重なっている答え"
      note={FAQ_CLEANUP_NOTE}
      extra={
        <span className="vd-row">
          {nothingToDo || report.overLimit ? null : (
            <Button
              variant="primary"
              disabled={mergeResult.fetching || confirming}
              onClick={() => setConfirming(true)}
            >
              {actionLabel}
            </Button>
          )}
          <HelpIcon slug="products/faq-cleanup" />
        </span>
      }
    >
      <div className="vd-stack">
        {userErrors.length === 0 ? null : (
          <div className="vd-state vd-state--error" role="alert">
            <span className="vd-state-title">片づけられませんでした</span>
            {userErrors.map((error) => (
              <p key={`${error.code}-${error.message}`} style={{ margin: 0 }}>
                {error.message}
              </p>
            ))}
          </div>
        )}

        {done === null ? null : (
          <div className="vd-notice" role="status">
            <strong>片づけました。</strong>
            {done === 0
              ? "並びをそろえ直しました（消えた答えはありません）。"
              : `${done.toLocaleString("ja-JP")} 件を消しました。`}
          </div>
        )}

        {report.overLimit ? (
          <div className="vd-notice vd-notice--error" role="alert">
            <strong>一度に片づけられません。</strong>
            消える答えは {report.removedCount.toLocaleString("ja-JP")} 件です。
            {FAQ_CLEANUP_OVER_LIMIT_NOTE}
          </div>
        ) : null}

        {nothingToDo ? (
          <EmptyState
            title="重なっている答えはありません"
            description={`いまある答えは ${report.faqTotal.toLocaleString("ja-JP")} 件で、同じ趣旨のものは含まれていません。`}
          />
        ) : (
          <>
            {removes ? (
              <p style={{ margin: 0 }}>
                いまある {report.faqTotal.toLocaleString("ja-JP")} 件のうち{" "}
                {report.removedCount.toLocaleString("ja-JP")} 件が消え、
                {report.remainingCount.toLocaleString("ja-JP")}{" "}
                件が残ります。残すのは、配信中の答えを優先し、同じなら新しいほうです。
              </p>
            ) : (
              <p style={{ margin: 0 }}>
                いまある {report.faqTotal.toLocaleString("ja-JP")}{" "}
                件はどれも重なっていません。ただし{" "}
                {report.rekeyedCount.toLocaleString("ja-JP")}{" "}
                件が古い並びのまま残っているので、そろえ直しておくと次に作る答えが重なりません。
                <strong>答えは 1 件も消えません。</strong>
              </p>
            )}

            {confirming ? (
              <div className="vd-notice" role="alert">
                <p style={{ margin: "0 0 8px" }}>
                  {removes ? (
                    <>
                      <strong>
                        {report.removedCount.toLocaleString("ja-JP")}{" "}
                        件を消します。
                      </strong>
                      元に戻せません。
                    </>
                  ) : (
                    <>
                      <strong>答えは 1 件も消えません。</strong>
                      並びをそろえ直すだけです。
                    </>
                  )}
                </p>
                <div className="vd-row">
                  <Button
                    variant="primary"
                    disabled={mergeResult.fetching}
                    onClick={() => {
                      void run();
                    }}
                  >
                    {mergeResult.fetching
                      ? "実行しています"
                      : removes
                        ? "消す"
                        : "そろえ直す"}
                  </Button>
                  <Button onClick={() => setConfirming(false)}>やめる</Button>
                </div>
              </div>
            ) : null}

            <div className="vd-stack">
              {report.groups.map((group) => (
                <div
                  key={group.dedupKey}
                  style={{
                    borderTop: "1px solid var(--vd-border)",
                    paddingTop: 12,
                  }}
                >
                  <div className="vd-card-title">
                    根拠: {group.evidenceLabel}
                  </div>
                  <p style={{ margin: "4px 0" }}>
                    残す: {group.keep.question}{" "}
                    {group.keep.delivered ? (
                      <Tag tone="ok">配信中</Tag>
                    ) : (
                      <Tag>未配信</Tag>
                    )}
                  </p>
                  {group.remove.length === 0 ? (
                    <p className="vd-stat-note" style={{ margin: 0 }}>
                      消える答えはありません（並びをそろえ直すだけです）。
                    </p>
                  ) : (
                    <ul className="vd-job-reasons">
                      {group.remove.map((entry) => (
                        <li key={entry.faqId}>
                          消す: {entry.question}（
                          {formatJstDateTime(entry.generatedAt)}）
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
